import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { MdMarkEmailRead, MdCheckCircle, MdErrorOutline, MdLogin } from 'react-icons/md';
import { verifyToken } from 'redux/authSlice';
import { getProfileUser } from 'redux/profileUserSlice';
import { approveBooking } from 'redux/booking/travel/travelBookingSlice';

const STATE_VERIFYING = 'verifying';
const STATE_APPROVING = 'approving';
const STATE_OPENING = 'opening';
const STATE_FAILED = 'failed';

// What each phase says while it runs. Naming the current step matters more here
// than anywhere else in the portal: the person arrived by clicking Approve in an
// email and is watching a screen they did not ask for, so it has to say what it
// is doing on their behalf — particularly that it is about to approve something.
const PHASES = {
  [STATE_VERIFYING]: {
    icon: MdMarkEmailRead,
    title: 'Signing you in',
    detail: 'Checking the secure link from your email.',
  },
  [STATE_APPROVING]: {
    icon: MdCheckCircle,
    title: 'Approving the request',
    detail: 'This only takes a moment.',
  },
  [STATE_OPENING]: {
    icon: MdCheckCircle,
    title: 'Done — opening the request',
    detail: 'Taking you to the booking.',
  },
};

const readTarget = (bookingUrl) => {
  if (!bookingUrl) return null;

  try {
    const decoded = decodeURIComponent(bookingUrl);
    const url = new URL(decoded);

    return {
      decoded,
      action: url.searchParams.get('type'),
      bookingId: decoded.match(/\/booking\/travel\/(\d+)/)?.[1] || null,
    };
  } catch {
    // A link mangled by an email client is not something the person can fix by
    // being shown a stack trace; sign-in still succeeded, so fall through to the
    // portal rather than failing the whole page.
    return null;
  }
};

/**
 * Landing page for the Approve / Reject links in booking emails.
 *
 * The link carries a signed token; this page exchanges it for a session, runs
 * the action the link asked for, and forwards to the booking.
 */
const VerifyEmail = () => {
  const dispatch = useDispatch();
  const [phase, setPhase] = useState(STATE_VERIFYING);

  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');
  const bookingUrl = searchParams.get('booking_url');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        setPhase(STATE_FAILED);
        return;
      }

      try {
        // unwrap() is what makes a rejection reach this catch. Without it the
        // dispatch resolves either way, the catch never runs, and a link that
        // had expired left the page saying "Verifying…" for ever while the only
        // hint of failure was a toast that had already faded.
        await dispatch(verifyToken({ token })).unwrap();
        await dispatch(getProfileUser());
        if (cancelled) return;

        const target = readTarget(bookingUrl);
        if (!target) {
          window.location.href = '/';
          return;
        }

        if (target.action === 'approve' && target.bookingId) {
          setPhase(STATE_APPROVING);

          // Its own catch: the sign-in worked, so "this link is no longer
          // valid" would be a false statement. A refused approval — already
          // approved, cancelled, no longer this person's to approve — is
          // something they resolve on the booking itself, so send them there.
          try {
            await dispatch(approveBooking(target.bookingId)).unwrap();
          } catch {
            if (cancelled) return;
            window.location.href = target.decoded;
            return;
          }
          if (cancelled) return;

          setPhase(STATE_OPENING);
          window.location.href = target.decoded.split('?')[0];
          return;
        }

        setPhase(STATE_OPENING);
        window.location.href = target.decoded;
      } catch {
        if (!cancelled) setPhase(STATE_FAILED);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [dispatch, token, bookingUrl]);

  if (phase === STATE_FAILED) {
    return (
      <div className="tw-text-center">
        <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-14 tw-h-14 tw-mb-4 tw-rounded-xl tw-bg-amber-50">
          <MdErrorOutline size={28} className="tw-text-amber-500" />
        </div>

        <h5 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-slate-800">
          This link is no longer valid
        </h5>

        {/* Says why and what happens next, because "invalid token" tells
            somebody nothing about whether their approval went through. */}
        <p className="tw-mb-1 tw-text-sm tw-text-slate-500">
          Links in booking emails stop working after seven days, or once they have
          already been used.
        </p>
        <p className="tw-mb-5 tw-text-sm tw-text-slate-500">
          Nothing has been approved or rejected. Sign in and the request will be
          waiting for you.
        </p>

        <Link
          to={process.env.REACT_APP_CLIENT_LOGIN_URL || '/authentication/login'}
          className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded tw-bg-blue-600 tw-text-white tw-text-sm tw-font-semibold tw-no-underline hover:tw-bg-blue-700 hover:tw-text-white tw-transition-colors"
        >
          <MdLogin size={16} /> Sign in to the portal
        </Link>
      </div>
    );
  }

  const { icon: Icon, title, detail } = PHASES[phase];

  return (
    <div className="tw-text-center">
      <div className="tw-relative tw-inline-flex tw-items-center tw-justify-center tw-w-14 tw-h-14 tw-mb-4">
        {/* The ring is the progress indicator; a separate spinner underneath
            the text made the page look like two things were loading. */}
        <span className="tw-absolute tw-inset-0 tw-rounded-xl tw-border-2 tw-border-solid tw-border-slate-100" />
        <span className="tw-absolute tw-inset-0 tw-rounded-xl tw-border-2 tw-border-solid tw-border-transparent tw-border-t-blue-600 tw-animate-spin" />
        <Icon size={24} className="tw-text-blue-600" />
      </div>

      <h5 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-slate-800">{title}</h5>
      <p className="tw-mb-0 tw-text-sm tw-text-slate-500">{detail}</p>
    </div>
  );
};

export default VerifyEmail;
